import sparePartsApi, { ApiResponse } from "../../../../api/spareParts.api";

import {
  OperationFilterOption,
  OperationCreateForm,
  OperationFilters,
  OperationInventoryStock,
  OperationRecord,
  OperationRecordsByTab,
  OperationSummary,
  OperationTab,
} from "../types";

const emptyRecords: OperationRecordsByTab = {
  requests: [],
  transfers: [],
  receipts: [],
  issues: [],
  returns: [],
  spareUsage: [],
  invoices: [],
};

const listParams = (filters: OperationFilters) => ({
  page: 1,
  limit: 100,
  search: filters.search || undefined,
  status: filters.status || undefined,
  warehouseId: filters.warehouseId || undefined,
  hubId: filters.warehouseId || undefined,
  technicianId: filters.technicianId || undefined,
  employeeId: filters.technicianId || undefined,
});

const unwrapRows = (payload: unknown): Record<string, unknown>[] => {
  const data = payload as { data?: unknown };

  if (Array.isArray(data?.data)) return data.data as Record<string, unknown>[];
  if (Array.isArray(payload)) return payload as Record<string, unknown>[];

  return [];
};

const text = (...values: unknown[]) =>
  values
    .map((value) => (value === null || value === undefined ? "" : String(value)))
    .find(Boolean);

const labelById = (options: OperationFilterOption[]) =>
  new Map(options.map((option) => [option.id, option.name]));

const mergedLabelById = (...optionGroups: OperationFilterOption[][]) =>
  new Map(optionGroups.flat().map((option) => [option.id, option.name]));

const numberValue = (...values: unknown[]) => {
  const value = values.find((item) => item !== null && item !== undefined);
  const parsed = Number(value || 0);

  return Number.isFinite(parsed) ? parsed : 0;
};

const nested = (row: Record<string, unknown>, key: string) =>
  (row[key] || {}) as Record<string, unknown>;

const sumItems = (row: Record<string, unknown>) => {
  const items = (row.items || row.spares || row.usages) as
    | Record<string, unknown>[]
    | undefined;

  if (!Array.isArray(items)) {
    return numberValue(
      row.quantity,
      row.quantityUsed,
      row.totalQuantity,
      row.requestedQuantity,
      row.quantityReceived,
      row.receivedQuantity,
      row.returnedQty,
      row.acceptedQty
    );
  }

  return items.reduce(
    (total, item) =>
      total +
      numberValue(
        item.quantity,
        item.quantityRequested,
        item.quantityDispatched,
        item.requestedQuantity,
        item.quantityReceived,
        item.receivedQuantity,
        item.returnedQty,
        item.acceptedQty
      ),
    0
  );
};

const amountValue = (row: Record<string, unknown>) => {
  const directAmount = numberValue(
    row.totalAmount,
    row.totalCost,
    row.invoiceAmount,
    row.amount
  );

  if (directAmount > 0) return directAmount;

  const items = row.items as Record<string, unknown>[] | undefined;

  if (Array.isArray(items)) {
    const itemTotal = items.reduce((total, item) => {
      const purchaseBillItem = nested(item, "purchaseBillItem");
      const totalPrice = numberValue(item.totalPrice, purchaseBillItem.totalPrice);

      if (totalPrice > 0) return total + totalPrice;

      const quantity = numberValue(
        item.quantity,
        item.quantityReceived,
        item.quantityUsed,
        item.acceptedQty
      );
      const unitCost = numberValue(
        item.unitCost,
        item.unitPrice,
        purchaseBillItem.unitPrice
      );

      return total + quantity * unitCost;
    }, 0);

    if (itemTotal > 0) return itemTotal;
  }

  const quantity = numberValue(row.quantity, row.quantityUsed);
  const unitCost = numberValue(row.unitCost, row.unitPrice);

  return quantity * unitCost;
};

const mapRecord = (
  row: Record<string, unknown>,
  type: OperationTab,
  module: string,
  lookups?: {
    hubsById?: Map<string, string>;
    techniciansById?: Map<string, string>;
    ticketsById?: Map<string, string>;
  }
): OperationRecord => {
  const ticket = nested(row, "ticket");

  return {
  id: String(row.id),
  code:
    text(
      row.requestCode,
      row.requestNumber,
      row.transferCode,
      row.transferNumber,
      row.receiptCode,
      row.receiptNumber,
      row.issueCode,
      row.issueNumber,
      row.returnCode,
      row.returnNumber,
      row.invoiceNumber,
      row.invoiceCode,
      row.usageCode,
      row.code,
      row.billNumber,
      row.id
    ) || "-",
  type,
  module,
  title:
    text(
      nested(row, "sparePart").name,
      nested(row, "sparePart").code,
      row.title,
      row.reason,
      row.remarks,
      row.issueTitle,
      row.description,
      module
    ) ||
    module,
  status: text(row.status, row.recordStatus) || (type === "spareUsage" ? "RECORDED" : "UNKNOWN"),
  source: text(row.sourceHubId, row.fromHubId, row.fromWarehouseId, row.sourceWarehouseId),
  destination: text(
    row.destinationHubId,
    row.toHubId,
    row.toWarehouseId,
    row.destinationWarehouseId
  ),
  warehouseId: text(
    row.warehouseId,
    row.hubId,
    row.requestHubId,
    row.receivedHubId,
    row.issueHubId,
    row.returnHubId,
    ticket.warehouseId
  ),
  sourceLabel:
    lookups?.hubsById?.get(
      text(row.sourceHubId, row.fromHubId, row.fromWarehouseId, row.sourceWarehouseId) ||
        ""
    ) ||
    text(row.sourceHubName, row.fromHubName, row.fromWarehouseName),
  destinationLabel:
    lookups?.hubsById?.get(
      text(
        row.destinationHubId,
        row.toHubId,
        row.toWarehouseId,
        row.destinationWarehouseId
      ) || ""
    ) ||
    text(row.destinationHubName, row.toHubName, row.toWarehouseName),
  warehouseLabel:
    lookups?.hubsById?.get(
      text(
        row.warehouseId,
        row.hubId,
        row.requestHubId,
        row.receivedHubId,
        row.issueHubId,
        row.returnHubId,
        ticket.warehouseId
      ) ||
        ""
    ) || text(row.warehouseName, row.hubName, row.receivedHubName, row.returnHubName, ticket.warehouseName),
  technicianId: text(
    row.technicianId,
    row.employeeId,
    row.assignedToId,
    row.receivedById,
    row.verifiedBy,
    row.updatedById,
    ticket.assignedToId
  ),
  technicianLabel:
    lookups?.techniciansById?.get(
      text(
        row.technicianId,
        row.employeeId,
        row.assignedToId,
        row.receivedById,
        row.verifiedBy,
        row.updatedById,
        ticket.assignedToId
      ) || ""
    ) || text(row.technicianName, row.employeeName),
  actorId: text(
    row.dispatchedById,
    row.requestedById,
    row.createdById,
    row.updatedById
  ),
  actorLabel:
    lookups?.techniciansById?.get(
      text(
        row.dispatchedById,
        row.requestedById,
        row.createdById,
        row.updatedById
      ) || ""
    ) || text(row.dispatchedByName, row.requestedByName, row.createdByName),
  vehicleId: text(row.vehicleId, row.vehicleNumber, ticket.vehicleId),
  ticketId: text(row.serviceTicketId, row.ticketId, ticket.id),
  ticketLabel:
    lookups?.ticketsById?.get(
      text(row.serviceTicketId, row.ticketId, ticket.id) || ""
    ) ||
    text(
      ticket.code,
      ticket.ticketNumber,
      row.ticketNumber,
      row.serviceTicketCode
    ),
  quantity: sumItems(row),
  amount: amountValue(row),
  createdAt: text(row.createdAt),
  updatedAt: text(row.updatedAt),
  raw: row,
  };
};

const getList = async (
  url: string,
  params: Record<string, unknown>,
  type: OperationTab,
  module: string,
  lookups?: {
    hubsById?: Map<string, string>;
    techniciansById?: Map<string, string>;
    ticketsById?: Map<string, string>;
  }
) => {
  const response = await sparePartsApi
    .get<ApiResponse<unknown>>(url, { params })
    .catch(() => ({ data: { data: [] } }));

  return unwrapRows(response.data.data).map((row) =>
    mapRecord(row, type, module, lookups)
  );
};

const optionName = (row: Record<string, unknown>) =>
  text(
    row.name,
    row.fullName,
    row.employeeName,
    row.user && `${(row.user as Record<string, unknown>).firstName || ""} ${(row.user as Record<string, unknown>).lastName || ""}`.trim(),
    `${row.firstName || ""} ${row.lastName || ""}`.trim(),
    row.employeeId,
    row.code,
    row.id
  ) || "-";

const employeeOption = (employee: Record<string, unknown>) => ({
  id: String(employee.employeeId || employee.employeeRecordId || employee.id),
  name: text(employee.employeeCode)
    ? `${text(employee.employeeName, optionName(employee))} (${employee.employeeCode})`
    : text(employee.employeeName, optionName(employee)) || "-",
  code: text(employee.employeeCode, employee.code),
});

const mapInventoryStock = (
  row: Record<string, unknown>,
  hubsById: Map<string, string>
): OperationInventoryStock => {
  const warehouseId = text(row.warehouseId, row.hubId) || "";

  return {
    id: String(row.id),
    warehouseId,
    warehouseLabel:
      hubsById.get(warehouseId) || text(row.warehouseName, row.hubName) || warehouseId,
    sparePartId: text(row.sparePartId, nested(row, "sparePart").id) || "",
    availableQuantity: numberValue(row.availableQuantity, row.availableStock),
  };
};

const rowItems = (record: OperationRecord): Record<string, unknown>[] => {
  const items = (record.raw.items || []) as Record<string, unknown>[];

  return Array.isArray(items) ? items : [];
};

class OperationsService {
  async getOptions(): Promise<{
    hubs: OperationFilterOption[];
    technicians: OperationFilterOption[];
    ames: OperationFilterOption[];
    spareParts: OperationFilterOption[];
    inventoryStocks: OperationInventoryStock[];
    stockRequests: OperationRecord[];
    stockTransfers: OperationRecord[];
    serviceTickets: OperationFilterOption[];
  }> {
    const [hubs, technicians, ames, spareParts, inventoryStocks, stockRequests, stockTransfers, serviceTickets] =
      await Promise.all([
      sparePartsApi
        .get<ApiResponse<unknown>>("/integrations/hubs")
        .catch(() => ({ data: { data: [] } })),
      sparePartsApi
        .get<ApiResponse<unknown>>("/integrations/technicians")
        .catch(() => ({ data: { data: [] } })),
      sparePartsApi
        .get<ApiResponse<unknown>>("/integrations/ames")
        .catch(() => ({ data: { data: [] } })),
      sparePartsApi
        .get<ApiResponse<unknown>>("/spare-parts/dropdown")
        .catch(() => ({ data: { data: [] } })),
      sparePartsApi
        .get<ApiResponse<unknown>>("/inventory/stocks", {
          params: { page: 1, limit: 1000 },
        })
        .catch(() => ({ data: { data: [] } })),
      getList(
        "/stock-requests",
        { page: 1, limit: 100 },
        "requests",
        "Stock Request"
      )
        .then((requests) =>
          requests.filter((request) => request.status.toUpperCase() === "REQUESTED")
        )
        .catch(() => []),
      getList(
        "/stock-transfers",
        { page: 1, limit: 100 },
        "transfers",
        "Stock Transfer"
      ).catch(() => []),
      sparePartsApi
        .get<ApiResponse<unknown>>("/maintenance/service-tickets", {
          params: { page: 1, limit: 100 },
        })
        .catch(() => ({ data: { data: [] } })),
    ]);

    const hubOptions = unwrapRows(hubs.data.data).map((hub) => ({
        id: String(hub.id),
        name: optionName(hub),
        code: text(hub.code),
        cityId: text(hub.cityId),
      }));
    const hubsById = labelById(hubOptions);

    return {
      hubs: hubOptions,
      technicians: unwrapRows(technicians.data.data).map(employeeOption),
      ames: unwrapRows(ames.data.data).map(employeeOption),
      spareParts: unwrapRows(spareParts.data.data).map((part) => ({
        id: String(part.id),
        name: text(part.name, part.spareName, part.partName, part.code) || "-",
        code: text(part.code, part.partNumber),
      })),
      inventoryStocks: unwrapRows(inventoryStocks.data.data).map((stock) =>
        mapInventoryStock(stock, hubsById)
      ),
      stockRequests,
      stockTransfers,
      serviceTickets: unwrapRows(serviceTickets.data.data).map((ticket) => ({
        id: String(ticket.id),
        name:
          text(ticket.code, ticket.ticketNumber, ticket.title, ticket.issueTitle, ticket.id) ||
          "-",
        code: text(ticket.code, ticket.ticketNumber),
      })),
    };
  }

  async getOperations(filters: OperationFilters): Promise<{
    records: OperationRecordsByTab;
    summary: OperationSummary;
  }> {
    const params = listParams(filters);
    const options = await this.getOptions().catch(() => ({
      hubs: [],
      technicians: [],
      ames: [],
      spareParts: [],
      inventoryStocks: [],
      stockRequests: [],
      stockTransfers: [],
      serviceTickets: [],
    }));
    const lookups = {
      hubsById: labelById(options.hubs),
      techniciansById: mergedLabelById(options.technicians, options.ames),
      ticketsById: labelById(options.serviceTickets),
    };
    const [
      requests,
      transfers,
      receipts,
      issues,
      returns,
      spareUsage,
      invoices,
    ] = await Promise.all([
      getList("/stock-requests", params, "requests", "Stock Request", lookups),
      getList("/stock-transfers", params, "transfers", "Stock Transfer", lookups),
      getList("/goods-receipts", params, "receipts", "Goods Receipt", lookups),
      getList(
        "/maintenance/spare-issues",
        params,
        "issues",
        "Spare Issue",
        lookups
      ),
      getList(
        "/maintenance/spare-returns",
        params,
        "returns",
        "Spare Return",
        lookups
      ),
      getList(
        "/maintenance/service-spare-usages",
        params,
        "spareUsage",
        "Service Spare Usage",
        lookups
      ),
      getList("/maintenance/service-invoices", params, "invoices", "Service Invoice", lookups),
    ]);

    const records = {
      ...emptyRecords,
      requests,
      transfers,
      receipts,
      issues,
      returns,
      spareUsage,
      invoices,
    };

    return {
      records,
      summary: this.buildSummary(records),
    };
  }

  async createOperation(form: OperationCreateForm) {
    const item = form.item;
    const createdById = form.employeeId;
    const createMap: Record<OperationTab, () => Promise<unknown>> = {
      requests: () =>
        sparePartsApi.post("/stock-requests", {
          requestHubId: form.hubId,
          requestedById: form.employeeId,
          priority: form.priority || "MEDIUM",
          remarks: form.remarks || undefined,
          createdById,
          items: form.items
            .filter((requestItem) => requestItem.sparePartId)
            .map((requestItem) => ({
              sparePartId: requestItem.sparePartId,
              quantityRequested: Number(requestItem.quantity),
              remarks: requestItem.remarks || undefined,
            })),
        }),
      transfers: () =>
        sparePartsApi.post("/stock-transfers", {
          stockRequestId: form.stockRequestId,
          sourceHubId: form.sourceHubId,
          destinationHubId: form.destinationHubId,
          remarks: form.remarks || undefined,
          createdById,
        }),
      receipts: () =>
        sparePartsApi.post("/goods-receipts", {
          stockTransferId: form.stockTransferId,
          receivedHubId: form.hubId,
          receivedById: form.employeeId,
          remarks: form.remarks || undefined,
          createdById,
          items: [
            {
              stockTransferItemId: item.stockTransferItemId,
              sparePartId: item.sparePartId,
              quantityReceived: Number(item.quantity),
              damagedQuantity: Number(item.damagedQuantity || 0),
              missingQuantity: Number(item.missingQuantity || 0),
              remarks: item.remarks || undefined,
            },
          ],
        }),
      issues: () =>
        Promise.reject(
          new Error("Spare issues are created from Maintenance ticket Needs Spare.")
        ),
      returns: () =>
        Promise.reject(
          new Error("Create spare returns from the Maintenance ticket Return Spare action.")
        ),
      spareUsage: () =>
        sparePartsApi.post("/maintenance/service-spare-usages", {
          ticketId: form.serviceTicketId,
          sparePartId: item.sparePartId,
          hubId: form.hubId,
          employeeId: form.employeeId,
          quantityUsed: Number(item.quantity),
          unitCost: Number(item.unitCost || 0),
          remarks: form.remarks || undefined,
          createdById,
        }),
      invoices: () =>
        sparePartsApi.post("/maintenance/service-invoices", {
          ticketId: form.serviceTicketId,
          labourCharge: Number(form.labourCharge || 0),
          discountAmount: Number(form.discountAmount || 0),
          taxAmount: Number(form.taxAmount || 0),
          paymentMode: form.paymentMode || undefined,
          status: form.invoiceStatus || "PENDING",
          remarks: form.remarks || undefined,
        }),
    };

    await createMap[form.tab]();
  }

  async runAction(
    record: OperationRecord,
    action: string,
    remarks = "",
    actorId: string
  ) {
    const transferReceiveItems = rowItems(record).map((item) => ({
      stockTransferItemId: String(item.id),
      quantityReceived: numberValue(item.quantityDispatched, item.quantity),
    }));

    const spareReturnVerifyItems = rowItems(record)
      .filter((item) => numberValue(item.returnedQty, 0) > 0)
      .map((item) => ({
        sparePartId: String(item.sparePartId || nested(item, "sparePart").id),
        acceptedQty: numberValue(item.returnedQty, 0),
        damagedQty: 0,
        remarks: remarks || undefined,
      }));

    const payloadMap: Record<string, Record<string, unknown>> = {
      approve: { approvedById: actorId },
      reject: {
        approvedById: actorId,
        rejectedBy: actorId,
        rejectedReason: remarks || "Rejected from dashboard",
      },
      cancel: { updatedById: actorId },
      dispatch: { dispatchedById: actorId },
      receive: {
        receivedById: actorId,
        remarks: remarks || undefined,
        items: transferReceiveItems,
      },
      verify:
        record.type === "returns"
          ? {
              verifiedBy: actorId,
              remarks: remarks || undefined,
              items: spareReturnVerifyItems,
            }
          : { updatedById: actorId, verifiedBy: actorId, remarks: remarks || undefined },
      issue:
        record.type === "requests"
          ? { issuedById: actorId }
          : { issuedById: actorId, remarks: remarks || undefined },
    };

    const actionMap: Record<OperationTab, Partial<Record<string, string>>> = {
      requests: {
        cancel: `/stock-requests/${record.id}/cancel`,
      },
      transfers: {
        dispatch: `/stock-transfers/${record.id}/dispatch`,
        receive: `/stock-transfers/${record.id}/receive`,
        cancel: `/stock-transfers/${record.id}/cancel`,
      },
      receipts: {
        verify: `/goods-receipts/${record.id}/verify`,
        cancel: `/goods-receipts/${record.id}/cancel`,
      },
      returns: {
        verify: `/maintenance/spare-returns/${record.id}/verify`,
        reject: `/maintenance/spare-returns/${record.id}/reject`,
      },
      issues: {
        issue: `/maintenance/spare-issues/${record.id}/issue`,
      },
      spareUsage: {},
      invoices: {},
    };

    const url = actionMap[record.type][action];

    if (!url) {
      throw new Error("This operation does not support the selected action.");
    }

    await sparePartsApi.patch(url, payloadMap[action]);
  }

  private buildSummary(records: OperationRecordsByTab): OperationSummary {
    const pendingStatuses = new Set([
      "PENDING",
      "REQUESTED",
      "PROCESSING",
      "DRAFT",
      "RAISED",
      "DISPATCHED",
    ]);

    const pendingActions = Object.values(records)
      .flat()
      .filter((row) => pendingStatuses.has(row.status.toUpperCase())).length;

    return {
      requests: records.requests.length,
      transfers: records.transfers.length,
      receipts: records.receipts.length,
      issues: records.issues.length,
      returns: records.returns.length,
      spareUsage: records.spareUsage.length,
      invoices: records.invoices.length,
      pendingActions,
    };
  }
}

export default new OperationsService();
